<script setup lang="ts">
import type { z } from "zod";
import type { Form } from "#ui/types";
import type { Response, FormSubmit, Country, Client } from "~/types";

interface SignInResponse {
  mobile: string | number;
  country_id: number;
  full_mobile_number: string;
  verification_token: string;
}

const { t, locale } = useI18n();
const toast = useToast();
const client = useClient();
const { signIn, checkOtpVerificationCode, resendOtpVerificationCode, completeProfile } =
  useAuth();
const { signInSchema, verificationCodeSchema, profileSchema } = useValidation();
const { checkDigit } = useFormater();

const globalStore = useGlobalStore();
const {
  signInModalOpen: open,
  signInModalStep,
  signInModalOnSuccess,
} = storeToRefs(globalStore);

type PhoneSchema = z.output<typeof signInSchema>;
type OtpSchema = z.output<typeof verificationCodeSchema>;
type ProfileSchema = z.output<typeof profileSchema>;

type Step = "phone" | "phone-otp" | "complete-profile";

const step = ref<Step>("phone");

const countries = ref<Country[]>([]);
const selectedCountry = ref<Country | undefined>(undefined);
const isFetchingCountries = ref(false);

const phoneForm = ref<null | Form<PhoneSchema>>(null);
const otpForm = ref<null | Form<OtpSchema>>(null);
const profileForm = ref<null | Form<ProfileSchema>>(null);

const phoneData = ref<PhoneSchema>({ country_id: 0, mobile: "" });
const otpData = ref<OtpSchema>({ verification_code: "" });
const profileData = ref<ProfileSchema>({ name: "", email: "", gender: "" });

const verificationToken = ref<string>("");
const fullMobileNumber = ref<string>("");

const genders = computed(() => [
  { value: "male", label: t("male") },
  { value: "female", label: t("female") },
]);

const canResend = ref(true);
const timer = ref(0);
const timerInterval = ref<NodeJS.Timeout | null>(null);
const isResending = ref(false);

const timerDisplay = computed(() => {
  const minutes = Math.floor(timer.value / 60);
  const seconds = timer.value % 60;
  return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
});

const stopTimer = () => {
  if (timerInterval.value) {
    clearInterval(timerInterval.value);
    timerInterval.value = null;
  }
};

const startTimer = () => {
  stopTimer();
  canResend.value = false;
  timer.value = 3 * 60;
  timerInterval.value = setInterval(() => {
    if (timer.value > 0) {
      timer.value--;
    } else {
      canResend.value = true;
      stopTimer();
    }
  }, 1000);
};

const loadCountries = async () => {
  if (countries.value.length > 0) return;
  isFetchingCountries.value = true;
  try {
    const response = await $larafetch<Response<{ countries: Country[] }>>(
      `/api/${useRuntimeConfig().public.apiVersion}/countries`
    );
    countries.value = response.data?.countries ?? [];
    selectedCountry.value =
      countries.value.find((c) => c.id === client.value?.country_id) ??
      countries.value.find((c) => c.selected) ??
      countries.value[0];
    phoneData.value.country_id = selectedCountry.value?.id ?? 0;
  } catch (err) {
    console.error(err);
  } finally {
    isFetchingCountries.value = false;
  }
};

const resetState = () => {
  phoneData.value = { country_id: selectedCountry.value?.id ?? 0, mobile: "" };
  otpData.value = { verification_code: "" };
  profileData.value = { name: "", email: "", gender: "" };
  verificationToken.value = "";
  fullMobileNumber.value = "";
  canResend.value = true;
  timer.value = 0;
  stopTimer();
};

const emitSuccess = (c: Client) => {
  const cb = signInModalOnSuccess.value;
  globalStore.closeSignInModal();
  if (cb) cb(c);
};

// ----- Phone path (Step 1: enter mobile) -----
const { submit: submitPhone, inProgress: inProgressPhone } = useSubmit<
  Response<SignInResponse>,
  PhoneSchema
>(
  () => signIn(phoneData.value),
  {
    onSuccess: (response) => {
      verificationToken.value = response.data.verification_token;
      fullMobileNumber.value = response.data.full_mobile_number;
      step.value = "phone-otp";
      startTimer();
      toast.add({
        title: response.message ?? t("success.verification_code"),
        color: "green",
        icon: "i-approved-success",
      });
    },
  },
  phoneForm as FormSubmit<PhoneSchema>
);

// ----- Phone path (Step 2: enter OTP) -----
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const { submit: submitOtp, inProgress: inProgressOtp } = useSubmit<Response<any>, OtpSchema>(
  async () => {
    return checkOtpVerificationCode({
      mobile: phoneData.value.mobile,
      country_id: phoneData.value.country_id as number,
      verification_code: otpData.value.verification_code,
      verification_token: verificationToken.value,
    });
  },
  {
    onSuccess: (response) => {
      client.value = response.data;
      stopTimer();

      if (response.data?.profile_completed) {
        toast.add({
          title: response.message,
          color: "green",
          icon: "i-approved-success",
        });
        emitSuccess(response.data as Client);
        return;
      }

      // Phone signup with incomplete profile → in-modal complete-profile step.
      profileData.value = {
        name: response.data?.name ?? "",
        email: response.data?.email ?? "",
        gender: response.data?.gender ?? "",
      };
      step.value = "complete-profile";
      toast.add({
        title: response.message ?? t("success.complete_profile_information"),
        color: "green",
        icon: "i-approved-success",
      });
    },
  },
  otpForm as FormSubmit<OtpSchema>
);

const handleResendPhoneOtp = async () => {
  if (!canResend.value || isResending.value) return;
  isResending.value = true;
  try {
    const response = await resendOtpVerificationCode({
      mobile: phoneData.value.mobile,
      country_id: phoneData.value.country_id,
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((response as any)?.code !== 200) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newToken = (response as any)?.data?.verification_token;
    if (newToken) verificationToken.value = newToken;
    startTimer();
    toast.add({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      title: (response as any)?.message ?? t("success.verification_code"),
      color: "green",
      icon: "i-approved-success",
    });
  } catch (err) {
    console.error(err);
  } finally {
    isResending.value = false;
  }
};

// ----- Phone path (Step 3: complete profile inline) -----
const { submit: submitProfile, inProgress: inProgressProfile } = useSubmit<
  Response<{ client: Client }>,
  ProfileSchema
>(
  () => completeProfile(profileData.value),
  {
    onSuccess: (response) => {
      client.value = response.data.client;
      toast.add({
        title: t("success.complete_profile"),
        color: "green",
        icon: "i-approved-success",
      });
      emitSuccess(response.data.client);
    },
  },
  profileForm as FormSubmit<ProfileSchema>
);

// ----- Dialog open/close lifecycle -----
watch(
  signInModalStep,
  () => {
    step.value = "phone";
  },
  { immediate: true }
);

watch(
  open,
  async (val) => {
    if (val) {
      resetState();
      step.value = "phone";
      await loadCountries();
    } else {
      stopTimer();
    }
  },
  { immediate: true }
);

watch(selectedCountry, () => {
  phoneData.value.country_id = selectedCountry.value?.id ?? 0;
});

onUnmounted(stopTimer);

const closeViaUser = () => {
  globalStore.closeSignInModal();
};

const goBackToPhoneEntry = () => {
  if (inProgressOtp.value || isResending.value) return;
  step.value = "phone";
  otpData.value.verification_code = "";
  stopTimer();
};

const headerTitle = computed(() => {
  switch (step.value) {
    case "phone":
      return t("page.sign_in.title");
    case "phone-otp":
      return t("page.mobile_verification.title");
    case "complete-profile":
      return t("page.complete_account.title");
    default:
      return "";
  }
});

const showBackButton = computed(() => step.value === "phone-otp");

const handleBack = () => {
  if (step.value === "phone-otp") goBackToPhoneEntry();
};
</script>

<template>
  <CommonBottomSheet
    v-model:open="open"
    fit-content
    :title="headerTitle">
    <div class="px-4 xs:px-5 pb-6 xs:pb-8">
      <div class="flex items-center justify-between gap-2 mb-5">
        <div class="flex items-center gap-x-2">
          <UButton
            v-if="showBackButton"
            icon="i-approved-arrow-left"
            size="sm"
            color="gray"
            variant="ghost"
            class="rounded-lg"
            :disabled="inProgressOtp || isResending"
            @click="handleBack" />
          <h2 class="text-base font-semibold text-gray-900 ltr:first-letter:uppercase">
            {{ headerTitle }}
          </h2>
        </div>
        <UButton
          icon="i-approved-close"
          size="sm"
          color="gray"
          variant="ghost"
          class="rounded-lg"
          @click="closeViaUser" />
      </div>

      <!-- STEP: phone (initial; entry point from Sign-in buttons) -->
      <template v-if="step === 'phone'">
        <p class="text-sm text-gray-500 ltr:first-letter:uppercase mb-5">
          {{ t("page.sign_in.desc") }}
        </p>
        <UForm
          ref="phoneForm"
          :state="phoneData"
          :schema="signInSchema"
          @submit="submitPhone">
          <div class="flex flex-col space-y-5">
            <UFormGroup name="mobile">
              <UInput
                v-model="phoneData.mobile"
                type="text"
                variant="outline"
                placeholder="000 00 000"
                dir="ltr"
                size="xl"
                inputmode="numeric"
                :ui="{
                  padding: { xl: 'px-3.5 py-3' },
                  leading: { padding: { xl: 'ps-27.5' } },
                  icon: {
                    leading: {
                      pointer: '',
                      wrapper: 'flex item-center absolute top-1/2 -translate-y-2/4 ltr:start-0 rtl:end-0 rtl:pe-3 rtl:ps-0 ltr:pe-0',
                    },
                  },
                }"
                @keydown="checkDigit">
                <template #leading>
                  <div class="flex items-center" dir="ltr">
                    <UFormGroup name="country_id">
                      <USelectMenu
                        v-model="selectedCountry"
                        :options="countries"
                        :disabled="inProgressPhone"
                        :popper="{ placement: 'bottom-start' }"
                        :padded="false"
                        size="xl"
                        by="id"
                        :ui="{
                          trailing: { padding: { xl: 'pe-6' } },
                          color: { white: { outline: 'text-gray-900' } },
                          icon: { trailing: { padding: { xl: 'py-3 px-0' } } },
                        }"
                        :ui-menu="{ width: 'w-80' }">
                        <template #label>
                          <USkeleton v-if="isFetchingCountries" class="w-15 h-8 rounded-md me-1.5" />
                          <div v-else class="flex items-center overflow-hidden">
                            <img v-if="selectedCountry?.image" class="size-6 rounded-md me-1.5" :src="selectedCountry?.image" :alt="selectedCountry?.name" >
                            <span class="inline-block text-center min-w-9.5">+{{ selectedCountry?.dialing_code }}</span>
                          </div>
                        </template>
                        <template #option="{ option }">
                          <div class="flex items-center overflow-hidden">
                            <img v-if="option?.image" class="size-6 rounded-md" :src="option?.image" :alt="option?.name" >
                            <span class="capitalize ml-1.5 mr-1">{{ option?.name }}</span>
                            <span class="inline-block">(+{{ option?.dialing_code }})</span>
                          </div>
                        </template>
                      </USelectMenu>
                    </UFormGroup>
                  </div>
                </template>
              </UInput>
            </UFormGroup>
            <UButton
              type="submit"
              :loading="inProgressPhone"
              :label="t('label.continue')"
              size="xl"
              class="rounded-lg"
              block />
          </div>
        </UForm>
      </template>

      <!-- STEP: phone-otp -->
      <template v-else-if="step === 'phone-otp'">
        <p class="text-sm text-gray-500 ltr:first-letter:uppercase mb-1">
          {{ t("page.mobile_verification.desc1") }}
          <span class="font-semibold text-primary" dir="ltr">{{ fullMobileNumber.trim() }}</span>
        </p>
        <p class="text-sm text-gray-500 ltr:first-letter:uppercase mb-5">
          {{ t("page.mobile_verification.desc2") }}
        </p>
        <UForm
          ref="otpForm"
          :state="otpData"
          :schema="verificationCodeSchema"
          @submit="submitOtp">
          <div class="flex flex-col space-y-5 mb-5">
            <UFormGroup name="verification_code" class="flex justify-center" dir="ltr">
              <CommonOtpInput v-model="otpData.verification_code" @complete="submitOtp" />
            </UFormGroup>
            <UButton
              type="submit"
              :loading="inProgressOtp"
              :label="t('label.verify')"
              size="xl"
              class="rounded-lg"
              block />
          </div>
        </UForm>
        <div class="flex justify-center items-center">
          <template v-if="canResend">
            <span class="text-sm text-gray-500 ltr:first-letter:uppercase">{{ t("page.mobile_verification.did_not_receive_verification_code") }}&nbsp;</span>
            <UButton :loading="isResending" type="button" :label="t('label.resend_again')" variant="link" :padded="false" @click="handleResendPhoneOtp" />
          </template>
          <span v-else class="text-sm text-gray-500 ltr:first-letter:uppercase">
            {{ t("page.mobile_verification.resend_again") }}
            <span class="text-primary font-medium w-9 text-center inline-block">0{{ timerDisplay }}</span>
          </span>
        </div>
      </template>

      <!-- STEP: complete-profile (phone path only) -->
      <template v-else-if="step === 'complete-profile'">
        <p class="text-sm text-gray-500 ltr:first-letter:uppercase mb-5">
          {{ t("page.complete_account.desc") }}
        </p>
        <UForm
          ref="profileForm"
          :state="profileData"
          :schema="profileSchema"
          @submit="submitProfile">
          <div class="flex flex-col space-y-5">
            <UFormGroup
              :label="t('label.full_name')"
              :hint="t('required')"
              name="name"
              size="xl">
              <UInput
                v-model="profileData.name"
                type="text"
                :placeholder="t('label.full_name')"
                size="xl"
                :ui="{ padding: { xl: 'px-3.5 py-3' } }" />
            </UFormGroup>
            <UFormGroup :label="t('label.email')" name="email" size="xl">
              <UInput
                v-model="profileData.email"
                type="text"
                :placeholder="t('label.email')"
                :class="{ 'placeholder-right': locale === 'ar' }"
                dir="ltr"
                size="xl"
                :ui="{ padding: { xl: 'px-3.5 py-3' } }" />
            </UFormGroup>
            <UFormGroup :label="t('label.gender')" name="gender" size="xl">
              <URadioGroup v-model="profileData.gender" :options="genders" />
            </UFormGroup>
            <UButton
              type="submit"
              :loading="inProgressProfile"
              :label="t('label.complete_profile')"
              size="xl"
              class="rounded-lg"
              block />
          </div>
        </UForm>
      </template>
    </div>
  </CommonBottomSheet>
</template>
